import React from 'react';
import { AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './dialog';
import { Button } from './button';
import { cn } from '../../lib/utils';

// El modal de aviso/confirmacion anterior a shadcn. Conserva su API (isOpen, title,
// message, type, onConfirm, onCancel, showCancel) porque seis pantallas lo llaman con un
// estado `modal` propio, pero desde 2026-09-05 se pinta sobre el Dialog de la base: era
// un portal hecho a mano, con su propio fondo y su propia tarjeta, y no heredaba nada de
// lo que la base ya resuelve (tema, foco, escape, pantalla completa en telefono).
// Va `compact`: es una pregunta de dos lineas, sigue siendo tarjeta en telefono.
//
// Lo que sigue es migrar a cada pantalla a useConfirmDialog y retirar esto del todo.

const ICONOS = {
  success: { Icono: CheckCircle, color: 'text-green-600', fondo: 'bg-green-100 dark:bg-green-950' },
  warning: { Icono: AlertTriangle, color: 'text-yellow-600', fondo: 'bg-yellow-100 dark:bg-yellow-950' },
  error: { Icono: AlertCircle, color: 'text-red-600', fondo: 'bg-red-100 dark:bg-red-950' },
  confirm: { Icono: Info, color: 'text-blue-600', fondo: 'bg-blue-100 dark:bg-blue-950' },
  info: { Icono: Info, color: 'text-blue-600', fondo: 'bg-blue-100 dark:bg-blue-950' },
};

const BOTON = {
  error: 'bg-red-500 hover:bg-red-600 text-white',
  success: 'bg-green-500 hover:bg-green-600 text-white',
  warning: 'bg-yellow-500 hover:bg-yellow-600 text-white',
};

const Modal = ({
  isOpen,
  onClose,
  title,
  message,
  type = 'info', // 'info', 'success', 'warning', 'error', 'confirm'
  confirmText = 'Aceptar',
  cancelText = 'Cancelar',
  onConfirm,
  onCancel,
  showCancel = false
}) => {
  const { Icono, color, fondo } = ICONOS[type] || ICONOS.info;

  // Cerrar por la X, por Escape o por el fondo cuenta como cancelar, igual que antes.
  const cancelar = () => {
    if (onCancel) onCancel();
    if (onClose) onClose();
  };

  const confirmar = () => {
    // Defensivo: ejecutar ambos para garantizar cierre incluso si hay closures stale o
    // re-renders del padre que afectan onConfirm.
    if (onConfirm) onConfirm();
    if (onClose) onClose();
  };

  return (
    <Dialog open={Boolean(isOpen)} onOpenChange={(open) => { if (!open) cancelar(); }}>
      <DialogContent compact className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', fondo)}>
              <Icono className={cn('h-6 w-6', color)} />
            </div>
            <DialogTitle className="text-left">{title}</DialogTitle>
          </div>
        </DialogHeader>
        <DialogDescription asChild>
          <div className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
            {typeof message === 'string' ? <p>{message}</p> : message}
          </div>
        </DialogDescription>
        <DialogFooter>
          {showCancel && (
            <Button type="button" variant="outline" onClick={cancelar}>
              {cancelText}
            </Button>
          )}
          <Button type="button" className={BOTON[type]} onClick={confirmar}>
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Modal;
